import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { roundHelper } from 'lib/helpers/round.helper.class';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

export type PricingInterval = 'monthly' | 'yearly';

type PricingIntervalButton = {
	readonly label: string;
	readonly interval: PricingInterval;
};

@Component({
	templateUrl: './pricing-card.component.html',
	selector: 'mod-pricing-card',
	styleUrls: ['./pricing-card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class PricingCardComponent extends TypeCoreComponent {
	public readonly buttonBehavior = input.required<'home' | 'subscribe'>();
	public readonly handleSubscribe = output<PricingInterval>();

	public readonly pricingInterval = signal<PricingInterval>('yearly');

	public readonly highlightFeaturesIndex: number = 1;
	public readonly getStartedUrl = computed<string>(() => {
		return this.dependencies.navigateService.signupPage().getUrl();
	});

	public readonly yearlyDiscountPercentage = computed<string | undefined>(() => {
		const { monthly, yearly } = this.pricing();

		const yearlyPercentDifference = (100 * yearly) / (monthly * 12);

		const discountPercentage = 100 - yearlyPercentDifference;

		return `Save ${roundHelper.roundTo(discountPercentage, 0)}%`;
	});

	public readonly pricingIntervalButtons = computed<readonly PricingIntervalButton[]>(() => {
		return [
			{
				label: `Yearly`,
				interval: 'yearly'
			},
			{
				label: 'Monthly',
				interval: 'monthly'
			}
		];
	});

	public readonly featureList: readonly string[] = [
		'Unlimited clients',
		'Branded subdomain',
		'Workout designer',
		'Diet designer',
		'Training schedule planning',
		'Nutrition schedule planning',
		'Custom branding',
		'File management',
		'Appointment scheduler',
		'Task management',
		'Live chat',
		'Progress tracking & graphs',
		'PDF download',
		'AI assistant',
		'Helpdesk',
		'And much more...'
	];

	readonly pricing = computed<{
		readonly monthly: number;
		readonly yearly: number;
	}>(() => {
		const { monthly, yearly } = this.appSetup()?.price ?? { monthly: '0', yearly: '0' };
		return {
			monthly: +monthly,
			yearly: +yearly
		};
	});

	public readonly activePrice = computed<{
		readonly price: string;
		readonly interval: PricingInterval;
		readonly discountPercentage?: string;
	}>(() => {
		return {
			price: this.pricingInterval() === 'monthly' ? `$${this.pricing().monthly}` : `$${this.pricing().yearly}`,
			interval: this.pricingInterval(),
			discountPercentage: this.pricingInterval() === 'yearly' ? this.yearlyDiscountPercentage() : undefined
		};
	});

	constructor() {
		super();
	}

	setPricingInterval(interval: PricingInterval): void {
		this.pricingInterval.set(interval);
	}
}
